import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Label } from '@/components/ui/Label';

interface Space {
  id: string;
  key: string;
  name: string;
  description?: string;
  isPublic: boolean;
}

interface SpaceMember {
  id: string;
  userId: string;
  role: string;
  user: {
    id: string;
    email: string;
    displayName: string;
  };
}

interface User {
  id: string;
  email: string;
  displayName: string;
}

async function fetchSpaceByKey(key: string): Promise<Space> {
  const token = useAuthStore.getState().accessToken;
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/spaces/key/${key}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  if (!response.ok) throw new Error('Failed to fetch space');
  return response.json();
}

async function fetchSpaceMembers(spaceId: string): Promise<SpaceMember[]> {
  const token = useAuthStore.getState().accessToken;
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/spaces/${spaceId}/members`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  if (!response.ok) throw new Error('Failed to fetch members');
  return response.json();
}

async function fetchAllUsers(): Promise<User[]> {
  const token = useAuthStore.getState().accessToken;
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/users`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  if (!response.ok) throw new Error('Failed to fetch users');
  return response.json();
}

async function addMember(spaceId: string, userId: string, role: string) {
  const token = useAuthStore.getState().accessToken;
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/spaces/${spaceId}/members`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, role }),
  });
  if (!response.ok) throw new Error('Failed to add member');
  return response.json();
}

async function removeMember(spaceId: string, userId: string) {
  const token = useAuthStore.getState().accessToken;
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/spaces/${spaceId}/members`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId }),
  });
  if (!response.ok) throw new Error('Failed to remove member');
  return response.json();
}

export function SpaceSettingsPage() {
  const { spaceKey } = useParams<{ spaceKey: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'general' | 'members'>('general');
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('viewer');

  // Fetch space details
  const { data: space, isLoading: spaceLoading } = useQuery<Space>({
    queryKey: ['space', spaceKey],
    queryFn: () => fetchSpaceByKey(spaceKey!),
    enabled: !!spaceKey,
  });

  // Fetch space members
  const { data: members, isLoading: membersLoading } = useQuery<SpaceMember[]>({
    queryKey: ['space-members', space?.id],
    queryFn: () => fetchSpaceMembers(space!.id),
    enabled: !!space?.id,
  });

  // Fetch all users for adding members
  const { data: allUsers } = useQuery<User[]>({
    queryKey: ['all-users'],
    queryFn: fetchAllUsers,
    enabled: showAddMemberDialog,
  });

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: () => addMember(space!.id, selectedUserId, selectedRole),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space-members', space?.id] });
      setShowAddMemberDialog(false);
      setSelectedUserId('');
      setSelectedRole('viewer');
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => removeMember(space!.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space-members', space?.id] });
    },
  });

  // Get users not already members
  const availableUsers = (allUsers || []).filter(
    user => !(members || []).some(member => member.user.id === user.id)
  );

  if (spaceLoading) {
    return (
      <div className="container mx-auto max-w-4xl py-8">
        <div className="flex items-center justify-center p-8">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!space) {
    return (
      <div className="container mx-auto max-w-4xl py-8">
        <Card>
          <div className="p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-900">Space ikke funnet</h2>
            <p className="mt-2 text-gray-600">
              Arbeidsområdet du prøver å nå eksisterer ikke eller du har ikke tilgang.
            </p>
            <Button onClick={() => navigate('/spaces')} className="mt-4">
              Tilbake til arbeidsområder
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{space.name}</h1>
          <p className="text-gray-600">Innstillinger for arbeidsområdet</p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/spaces/${spaceKey}`)}>
          Tilbake
        </Button>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('general')}
            className={`border-b-2 px-1 py-4 text-sm font-medium ${
              activeTab === 'general'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            Generelt
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`border-b-2 px-1 py-4 text-sm font-medium ${
              activeTab === 'members'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            Medlemmer
          </button>
        </nav>
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <Card>
          <div className="p-6">
            <h2 className="mb-4 text-xl font-semibold">Generelle innstillinger</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Nøkkel</label>
                <p className="text-gray-900">{space.key}</p>
                <p className="text-sm text-gray-500">
                  Unik identifikator for dette arbeidsområdet
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Navn</label>
                <p className="text-gray-900">{space.name}</p>
              </div>
              {space.description && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Beskrivelse</label>
                  <p className="text-gray-900">{space.description}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-700">Synlighet</label>
                <p className="text-gray-900">
                  {space.isPublic ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
                      🌍 Offentlig
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-800">
                      🔒 Privat
                    </span>
                  )}
                </p>
                <p className="text-sm text-gray-500">
                  {space.isPublic
                    ? 'Alle kan se dette arbeidsområdet'
                    : 'Kun medlemmer kan se dette arbeidsområdet'}
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Members Tab */}
      {activeTab === 'members' && (
        <Card>
          <div className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Medlemmer</h2>
              <Button variant="outline" size="sm" onClick={() => setShowAddMemberDialog(true)}>
                Legg til medlem
              </Button>
            </div>

            {membersLoading ? (
              <div className="flex justify-center p-8">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
              </div>
            ) : members && members.length > 0 ? (
              <div className="space-y-3">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {member.user.displayName}
                      </p>
                      <p className="text-sm text-gray-500">{member.user.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          member.role === 'admin'
                            ? 'bg-red-100 text-red-800'
                            : member.role === 'editor'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {member.role === 'admin'
                          ? 'Administrator'
                          : member.role === 'editor'
                          ? 'Redaktør'
                          : 'Leser'}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => removeMemberMutation.mutate(member.user.id)}
                        disabled={removeMemberMutation.isPending}
                      >
                        Fjern
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-8 text-center text-gray-500">
                Ingen medlemmer i dette arbeidsområdet
              </p>
            )}

            {/* Add Member Dialog */}
            {showAddMemberDialog && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <Card className="w-full max-w-md">
                  <div className="p-6">
                    <h3 className="mb-4 text-lg font-semibold">Legg til medlem</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="user-select">Bruker</Label>
                        <select
                          id="user-select"
                          value={selectedUserId}
                          onChange={(e) => setSelectedUserId(e.target.value)}
                          className="w-full rounded-md border p-2"
                        >
                          <option value="">Velg bruker...</option>
                          {availableUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.displayName} ({user.email})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <Label htmlFor="role-select">Rolle</Label>
                        <select
                          id="role-select"
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value)}
                          className="w-full rounded-md border p-2"
                        >
                          <option value="viewer">Leser</option>
                          <option value="editor">Redaktør</option>
                          <option value="admin">Administrator</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowAddMemberDialog(false);
                          setSelectedUserId('');
                          setSelectedRole('viewer');
                        }}
                      >
                        Avbryt
                      </Button>
                      <Button
                        onClick={() => addMemberMutation.mutate()}
                        disabled={!selectedUserId || addMemberMutation.isPending}
                      >
                        {addMemberMutation.isPending ? 'Legger til...' : 'Legg til'}
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
